import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app, { db } from '../firebase';

const nowIso = () => new Date().toISOString();
const clean = (value, fallback = '') => String(value ?? fallback).trim();

export const ACTIVE_SESSION_STATUSES = ['pending', 'paid', 'confirmed', 'active'];
export const FINAL_SESSION_STATUSES = ['completed', 'cancelled', 'refunded', 'expired'];
export const ACTIVE_BOOKING_LIMIT = 3;
export const FREE_WEEKLY_SLOT_LIMIT = 3;
export const SESSION_NAME_OPTIONS = [
  'Tarot reading',
  'Crystal ball',
  'Astrology',
  'Numerology',
  'Palm reading',
  'Coffee reading',
  'Dream reading',
];
const functions = getFunctions(app, 'europe-west1');
const createSessionBooking = httpsCallable(functions, 'createSessionBooking');
const confirmPayment = httpsCallable(functions, 'confirmSessionPayment');
const requestCancellation = httpsCallable(functions, 'requestSessionCancellation');
const resolveCancellation = httpsCallable(functions, 'resolveSessionCancellation');
const completeSession = httpsCallable(functions, 'completeSession');
const createSessionReview = httpsCallable(functions, 'createSessionReview');

export function toNumber(value, fallback = 0) {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

export function normalizeCurrency(value) {
  return String(value || 'EUR').trim().toUpperCase() === 'CZK' ? 'CZK' : 'EUR';
}

export function normalizeDurationMinutes(value) {
  const n = Number.parseInt(value, 10);
  if (n === 1) return 60;
  if (n === 2) return 120;
  if (n === 60 || n === 120) return n;
  return 60;
}

export function parseSessionStart(session) {
  const raw = session?.startTime || (session?.date && session?.startTimeOnly ? `${session.date}T${session.startTimeOnly}` : null);
  const fromCombined = new Date(String(raw || '')).getTime();
  if (Number.isFinite(fromCombined)) return new Date(fromCombined);
  if (session?.date && session?.startTime) {
    const fromLegacy = new Date(`${session.date}T${session.startTime}`).getTime();
    if (Number.isFinite(fromLegacy)) return new Date(fromLegacy);
  }
  return null;
}

export function formatDateTime(value) {
  if (!value) return '—';
  const start = typeof value === 'string' ? new Date(value) : parseSessionStart(value);
  if (!start || Number.isNaN(start.getTime())) return String(value?.startTime || value || '—');
  return new Intl.DateTimeFormat('sk-SK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(start);
}

export function formatTime(value) {
  const start = typeof value === 'string' ? new Date(value) : parseSessionStart(value);
  if (!start || Number.isNaN(start.getTime())) return '—';
  return new Intl.DateTimeFormat('sk-SK', { hour: '2-digit', minute: '2-digit' }).format(start);
}

export function sessionEnd(session) {
  const start = parseSessionStart(session);
  if (!start) return null;
  return new Date(start.getTime() + normalizeDurationMinutes(session?.duration) * 60000);
}

export function hoursUntil(session) {
  const start = parseSessionStart(session);
  if (!start) return Infinity;
  return (start.getTime() - Date.now()) / 3600000;
}

export function isFutureSession(session) {
  const end = sessionEnd(session);
  return end ? end.getTime() > Date.now() : true;
}

function weekStart(value) {
  const date = new Date(`${String(value || '').slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day + 1);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

export function canEnterRoom(session) {
  const start = parseSessionStart(session);
  const end = sessionEnd(session);
  if (!start || !end) return { ok: true };
  const now = Date.now();
  if (now < start.getTime() - 5 * 60000) {
    return { ok: false, code: 'early', label: `Room opens 5 minutes before ${formatDateTime(session)}` };
  }
  if (now > end.getTime() + 60000) {
    return { ok: false, code: 'ended', label: 'This session room has closed.' };
  }
  return { ok: true };
}

export function normalizeTeller(id, data = {}, source = 'users') {
  const category = Array.isArray(data.category) ? data.category : [];
  const skills = Array.isArray(data.skills) ? data.skills : category;
  const languages = Array.isArray(data.languages) ? data.languages : [data.language || 'English'].filter(Boolean);
  const name = clean(data.name || data.displayName || data.fullName || data.tutorName || data.email, 'Fate Room reader');
  return {
    id,
    source,
    userId: data.userId || data.uid || id,
    name,
    email: data.email || data.tutorEmail || '',
    bio: data.bio || data.description || data.aboutme || 'Independent reader offering live Fate Room sessions.',
    photo: data.photoUrl || data.profileImage || data.photo || '/assets/tarot-empress.jpg',
    skills,
    category,
    languages,
    price: toNumber(data.price ?? data.pricePerMinute ?? data.rate ?? 30),
    rating: toNumber(data.rating, 4.8),
    reviewsCount: Number.parseInt(data.reviewsCount || data.reviewCount || 0, 10) || 0,
    online: Boolean(data.online || data.onlineNow || data.isOnline),
    role: data.role || data.type || 'teller',
    approved: data.approved === true,
    approvalStatus: data.approvalStatus || (data.approved === true ? 'approved' : 'draft'),
    banned: data.banned === true,
    headline: data.headline || '',
    experience: toNumber(data.experience, 0),
    introVideoUrl: data.introVideoUrl || '',
    raw: data,
  };
}

export function normalizeSlot(id, data = {}, source = 'availability') {
  const startTime = data.startTime && String(data.startTime).includes('T')
    ? data.startTime
    : data.date && data.startTime
      ? `${data.date}T${data.startTime}`
      : data.startTime;
  return {
    id,
    source,
    tellerId: data.tellerId || data.tutorId,
    tutorId: data.tutorId || data.tellerId,
    tutorEmail: data.tutorEmail || data.tellerEmail || '',
    sessionName: data.sessionName || data.title || 'Reading',
    description: data.description || '',
    sessionType: data.sessionType || 'video',
    duration: normalizeDurationMinutes(data.duration),
    price: toNumber(data.price ?? 0),
    currency: normalizeCurrency(data.currency || data.currencyCode),
    startTime,
    status: data.status || (data.booked ? 'reserved' : 'available'),
    booked: Boolean(data.booked || data.status === 'reserved'),
    bookedBy: data.bookedBy || '',
    bookingId: data.bookingId || null,
    raw: data,
  };
}

function mergeById(lists, keySelector = (item) => item.id) {
  const map = new Map();
  lists.flat().forEach((item) => {
    if (!item?.id) return;
    const key = keySelector(item);
    if (!key) return;
    map.set(key, { ...(map.get(key) || {}), ...item });
  });
  return [...map.values()];
}

function normalizeSessionRecord(id, data = {}, source = 'sessions') {
  const rawStart = data.startTime || data.time || '';
  const startTime = String(rawStart).includes('T')
    ? rawStart
    : data.date && rawStart
      ? `${data.date}T${rawStart}`
      : rawStart;
  const completed = Boolean(data.completed || data.status === 'completed');
  const cancelled = Boolean(
    data.cancelled
    || data.cancelAccepted
    || data.cancelledByTeller
    || data.status === 'cancelled'
    || data.status === 'refunded'
  );

  return {
    id,
    source,
    ...data,
    startTime,
    duration: normalizeDurationMinutes(data.duration),
    price: toNumber(data.price ?? 0),
    currency: normalizeCurrency(data.currency || data.currencyCode),
    status: cancelled ? 'cancelled' : completed ? 'completed' : (data.status || 'paid'),
    sessionName: data.sessionName || data.title || 'Live reading',
    tellerName: data.tellerName || data.tutorName || data.tellerEmail || data.tutorEmail || 'Fate Room reader',
    tellerEmail: data.tellerEmail || data.tutorEmail || '',
    marketplaceSessionId: data.marketplaceSessionId || data.bookingId || (source === 'sessions' ? id : null),
    legacySessionId: data.legacySessionId || (source === 'availableSessions' ? id : data.sessionId || null),
    cancelRequested: Boolean(data.cancelRequested || data.cancelRequest || data.cancelStatus === 'pending'),
  };
}

export async function getProfile(uid, { includeTellerProfile = false } = {}) {
  if (!uid) return null;
  const [userSnap, tellerSnap] = await Promise.all([
    getDoc(doc(db, 'users', uid)).catch(() => null),
    includeTellerProfile
      ? getDoc(doc(db, 'fortuneTellers', uid)).catch(() => null)
      : Promise.resolve(null),
  ]);
  return {
    id: uid,
    ...(userSnap?.exists() ? userSnap.data() : {}),
    ...(tellerSnap?.exists() ? tellerSnap.data() : {}),
  };
}

export function subscribeTellers(callback, onError) {
  const buckets = {
    fortuneTellers: { rows: [], failed: false },
  };
  const emit = () => {
    const rows = mergeById([
      buckets.fortuneTellers.rows,
    ], (teller) => teller.userId || teller.id)
      .filter((t) => (
        t.approved === true
        && t.approvalStatus === 'approved'
        && t.banned !== true
        && (t.role === 'teller' || t.role === 'fortuneTeller' || t.source === 'fortuneTellers')
      ));
    callback(rows);

    const allFailed = Object.values(buckets).every((bucket) => bucket.failed);
    if (allFailed && rows.length === 0) {
      onError?.(new Error('Reader profiles could not be loaded because Firestore rules denied every public reader query.'));
    }
  };
  const unsubs = [];

  const attach = (key, q, source) => {
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        buckets[key] = {
          rows: snap.docs.map((d) => normalizeTeller(d.id, d.data(), source)),
          failed: false,
        };
        emit();
      },
      (error) => {
        // A denied optional query must not break Browse Tellers. Other readable
        // sources can still populate the page.
        buckets[key] = { rows: [], failed: true };
        onError?.(error);
        emit();
      },
    );
    unsubs.push(unsubscribe);
  };

  attach(
    'fortuneTellers',
    query(collection(db, 'fortuneTellers'), where('approved', '==', true), limit(80)),
    'fortuneTellers',
  );

  return () => unsubs.forEach((fn) => fn && fn());
}

export function subscribeTellerAvailability(
  tellerId,
  callback,
  onError,
  { includeReserved = false, includePast = false } = {},
) {
  if (!tellerId) return () => {};
  return onSnapshot(
    query(collection(db, 'availability'), where('tellerId', '==', tellerId), limit(80)),
    (snap) => {
      const list = snap.docs
        .map((slotDoc) => normalizeSlot(slotDoc.id, slotDoc.data(), 'availability'))
      .filter((slot) => includeReserved || slot.status === 'available')
      .filter((slot) => includePast || isFutureSession(slot))
      .sort((a, b) => String(a.startTime).localeCompare(String(b.startTime)));
      callback(list);
    },
    (error) => {
      callback([]);
      onError?.(error);
    },
  );
}

export function subscribeUserSessions(uid, callback, onError) {
  if (!uid) return () => {};

  // Marketplace bookings are canonical in /sessions. Querying the retired
  // collections from every dashboard caused permission errors even though the
  // user's current sessions were readable.
  return onSnapshot(
    query(collection(db, 'sessions'), where('userId', '==', uid), limit(60)),
    (snapshot) => {
      callback(snapshot.docs
        .map((sessionDoc) => normalizeSessionRecord(sessionDoc.id, sessionDoc.data(), 'sessions'))
        .sort((a, b) => String(a.startTime || '').localeCompare(String(b.startTime || ''))));
    },
    (error) => {
      callback([]);
      onError?.(new Error(
        error?.code === 'permission-denied'
          ? 'Your session access is not active yet. Sign out, sign in again, and retry.'
          : 'Your sessions could not be loaded. Please retry.',
      ));
    },
  );
}

export function subscribeTellerSessions(tellerId, email, callback, onError) {
  if (!tellerId) return () => {};
  return onSnapshot(
    query(collection(db, 'sessions'), where('tellerId', '==', tellerId), limit(80)),
    (snap) => callback(snap.docs
      .map((sessionDoc) => normalizeSessionRecord(sessionDoc.id, sessionDoc.data(), 'sessions'))
      .sort((a, b) => String(b.startTime || '').localeCompare(String(a.startTime || '')))),
    (error) => {
      callback([]);
      onError?.(error);
    },
  );
}

export async function createTellerAvailability({ user, profile, form }) {
  if (!user?.uid) throw new Error('You must be logged in as a reader.');
  if (!SESSION_NAME_OPTIONS.includes(form.sessionName)) {
    throw new Error('Choose a valid reading type.');
  }
  const duration = normalizeDurationMinutes(form.duration);
  if (![60, 120].includes(duration)) throw new Error('Session duration must be 1 or 2 hours.');
  if (!/^(?:[01]\d|2[0-3]):00$/.test(form.time || '')) {
    throw new Error('Sessions must start on the whole hour.');
  }
  const startTime = `${form.date}T${form.time}`;
  const start = new Date(startTime);
  if (Number.isNaN(start.getTime())) throw new Error('Choose a valid date and time.');
  if (start.getMinutes() !== 0) throw new Error('Sessions must start on the whole hour.');
  if (start.getTime() <= Date.now()) throw new Error('Create only future sessions.');
  const currency = String(form.currency || 'EUR').trim().toUpperCase();
  if (!['EUR', 'CZK'].includes(currency)) throw new Error('Choose EUR or CZK as the payment currency.');
  const price = toNumber(form.price);
  const maximumPrice = currency === 'CZK' ? 25000 : 1000;
  if (price < 1 || price > maximumPrice) throw new Error(`Price must be between 1 and ${maximumPrice} ${currency}.`);

  if (profile?.tellerSubscriptionPlan !== 'pro') {
    const existing = await getDocs(query(
      collection(db, 'availability'),
      where('tellerId', '==', user.uid),
      limit(160),
    ));
    const targetWeek = weekStart(startTime);
    const weeklyUsed = existing.docs
      .map((slotDoc) => slotDoc.data())
      .filter((slot) => (
        ['available', 'reserved'].includes(slot.status)
        && weekStart(slot.startTime) === targetWeek
      ))
      .length;
    if (weeklyUsed >= FREE_WEEKLY_SLOT_LIMIT) {
      throw new Error('Free readers can publish up to 3 session slots per week. Reader Pro removes this limit.');
    }
  }

  const ref = doc(collection(db, 'availability'));
  const payload = {
    tellerId: user.uid,
    tutorId: user.uid,
    tellerEmail: user.email,
    tutorEmail: user.email,
    tellerName: profile?.name || user.email,
    sessionName: clean(form.sessionName, 'Live reading'),
    description: clean(form.description, ''),
    sessionType: form.sessionType || 'video',
    duration: String(duration),
    price: String(price),
    currency,
    startTime,
    status: 'available',
    createdAt: nowIso(),
  };
  await setDoc(ref, payload);
  return { id: ref.id, ...payload };
}

export async function bookSlot({ slot, user, profile, teller }) {
  if (!user?.uid) throw new Error('Log in before booking a session.');
  if (!slot?.id) throw new Error('Choose an available session.');

  const slotTellerId = clean(slot.tellerId || slot.tutorId || teller?.userId || teller?.id);
  const slotTellerEmail = clean(
    slot.tellerEmail || slot.tutorEmail || teller?.email,
  ).toLowerCase();
  const userEmail = clean(user.email).toLowerCase();
  if (slotTellerId === user.uid || (slotTellerEmail && slotTellerEmail === userEmail)) {
    throw new Error('You cannot book your own session. Manage it from the reader dashboard.');
  }

  if (slot.source !== 'availability') {
    throw new Error('This legacy slot cannot be booked. Please choose a current availability slot.');
  }

  // The server reads the trusted slot and private teller payment profile,
  // then reserves both records atomically. Never trust browser-supplied price
  // or teller data for a payment reservation.
  const result = await createSessionBooking({ slotId: slot.id });
  return result.data.session;

}

export async function confirmSessionPayment(session, tellerUser) {
  const sessionId = session?.source === 'sessions'
    ? session.id
    : session?.marketplaceSessionId;
  if (!sessionId) {
    throw new Error('Payment request was not found.');
  }
  if (!tellerUser?.uid) throw new Error('Log in as the reader to confirm payment.');
  await confirmPayment({ sessionId });
}

export async function requestCancelSession(session, actor = 'client') {
  if (!session?.id) throw new Error('Session not found.');
  const sessionId = session.source === 'sessions' ? session.id : session.marketplaceSessionId;
  if (!sessionId) throw new Error('The linked session was not found.');
  await requestCancellation({ sessionId });
}

export async function approveCancelSession(session, actor = 'teller') {
  if (!session?.id) throw new Error('Session not found.');
  const sessionId = session.source === 'sessions' ? session.id : session.marketplaceSessionId;
  if (!sessionId) throw new Error('The linked session was not found.');
  await resolveCancellation({ sessionId, decision: 'approved' });
}

export async function declineCancelSession(session, actor = 'teller') {
  if (!session?.id) throw new Error('Session not found.');
  const sessionId = session.source === 'sessions' ? session.id : session.marketplaceSessionId;
  if (!sessionId) throw new Error('The linked session was not found.');
  await resolveCancellation({ sessionId, decision: 'declined' });
}

export async function markSessionCompleted(session, actor = 'teller') {
  if (!session?.id) throw new Error('Session not found.');
  await completeSession({ sessionId: session.id });
}

export async function getSessionById(id) {
  const sessionSnap = await getDoc(doc(db, 'sessions', id)).catch(() => null);
  if (sessionSnap?.exists()) return { id: sessionSnap.id, source: 'sessions', ...sessionSnap.data() };
  const bookingSnap = await getDoc(doc(db, 'bookings', id)).catch(() => null);
  if (bookingSnap?.exists()) return { id: bookingSnap.id, source: 'bookings', ...bookingSnap.data() };
  const legacySnap = await getDoc(doc(db, 'availableSessions', id)).catch(() => null);
  if (legacySnap?.exists()) return { id: legacySnap.id, source: 'availableSessions', ...legacySnap.data() };
  return null;
}

export function listenMessages(sessionId, callback, onError) {
  if (!sessionId) return () => {};
  return onSnapshot(
    query(collection(db, 'messages'), where('sessionId', '==', sessionId), limit(200)),
    (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(a.timestamp || a.createdAt || '').localeCompare(String(b.timestamp || b.createdAt || '')));
      callback(rows);
    },
    (error) => {
      onError?.(error);
      callback([]);
    },
  );
}

export async function sendChatMessage({ sessionId, senderId, text }) {
  const body = clean(text);
  if (!body) return null;
  return addDoc(collection(db, 'messages'), {
    sessionId,
    senderId,
    text: body.slice(0, 1000),
    timestamp: nowIso(),
    createdAt: nowIso(),
  });
}

export async function saveReview({ session, user, rating, comment }) {
  if (!session?.id || !user?.uid) throw new Error('Review cannot be saved.');
  const result = await createSessionReview({
    sessionId: session.id,
    rating: Number(rating),
    comment: clean(comment).slice(0, 800),
  });
  return result.data.reviewId;
}
